import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid as MuiGrid,
  Stack,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { AddOrUpdateBarcodeMappingRequest, BarcodeMappingDto } from '../../../../app/services/api/orderManagementClient';
import { Item, Uom } from '../../../../app/services/api/generated';
import { BarcodeScannerModal } from '../../../StockCount/components/BarcodeScanner/BarcodeScannerModal';
import { Camera01 } from '../../../../assets';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';

type Props = {
  storeNumber: string;
  open: boolean;
  mapping: BarcodeMappingDto | null;
  initialBarcode?: string;
  allowScanning: boolean;
  helperMessage?: string;
  onClose: () => void;
  onSave: (request: AddOrUpdateBarcodeMappingRequest) => Promise<void>;
};

const BarcodeMappingAddEditModal: FC<Props> = ({ storeNumber, open, mapping, onClose, onSave, initialBarcode, allowScanning, helperMessage }) => {
  const { t } = useTranslation('barcodeMapping');
  const { getItems, getUoms } = useNswagClient();

  const [itemNumber, setItemNumber] = useState('');
  const [barcode, setBarcode] = useState('');
  const [uom, setUom] = useState('');
  const [description, setDescription] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [itemOptions, setItemOptions] = useState<Item[]>([]);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [uomOptions, setUomOptions] = useState<Uom[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const madein = undefined;
  const stock = true;
  const product = undefined;
  const sale = undefined;
  const material = undefined;
  const activity = undefined;
  const isDailyWaste = undefined;
  const sort  = '';
  const direction = '';
  const addItems = undefined;
  const isStockCountEnabled = true;

  useEffect(() => {
    setErrorMessage('');
    setSnackbarOpen(false);
  }, [open]);

  useEffect(() => {
    setErrorMessage('');
    if (mapping) {
      setItemNumber(mapping.SKU);
      setBarcode(mapping.Barcode);
      setUom(mapping.UOM);
      setDescription(mapping.Name);
      fetchUomOptions(mapping.SKU);
    } else {
      setItemNumber('');
      setBarcode(initialBarcode || '');
      setUom('');
      setSelectedItem(null);
      setItemOptions([]);
      setUomOptions([]);
      setSearchTerm('');
    }
  }, [mapping, open]);

  useEffect(() => {
    let isCancelled = false;
    const debounceTimer = setTimeout(() => {
      const fetchItems = async () => {
        if (isCancelled) return;
        
        try {
          setIsLoading(true);
          const response = await getItems(searchTerm, storeNumber, activity, madein, stock, sale, product, material, true, isStockCountEnabled, isDailyWaste, sort, direction, 0, 100, addItems);
          
          // Only update state if this request hasn't been cancelled
          if (!isCancelled && response && response.data && response.data.items) {
            setItemOptions(response.data.items);
          } else if (!isCancelled) {
            setItemOptions(prev => (prev.length ? [] : prev));
          }
        } catch (error) {
          if (!isCancelled) {
            const msg =
            error && typeof error === 'object' && 'message' in error
              ? (error as Error).message
              : t('unknownError');
            setErrorMessage(msg);
          }
        } finally {
          if (!isCancelled) {
            setIsLoading(false);
          }
        }
      };
    
      if (!mapping && searchTerm.length >= 1) {
        fetchItems();
      } else {
        setItemOptions(prev => (prev.length > 0 ? [] : prev));
        setIsLoading(false);
      }
    }, 300); // Wait 300ms after user stops typing before making the request

    return () => {
      clearTimeout(debounceTimer);
      isCancelled = true;
    };
  }, [searchTerm, mapping]);

  const fetchUomOptions = async (itemNum: string) => {
    try {
      const response = await getUoms(itemNum, storeNumber, undefined, undefined, undefined);
      if (response && response.data && response.data.uoms) {
        setUomOptions(response.data.uoms); // assuming the API returns { uoms: string[] }
      }
    } catch (error) {
      const msg =
      error && typeof error === 'object' && 'message' in error
        ? (error as Error).message
        : t('unknownError');
      setErrorMessage(msg);
    }
  };

  useEffect(() => {
    if (selectedItem && selectedItem.itemNumber) {
      setItemNumber(selectedItem.itemNumber);
      fetchUomOptions(selectedItem.itemNumber);
    }
  }, [selectedItem]);

  useEffect(() => {
    // Allow alphanumeric, parentheses, and optionally the GS1-128 prefix
    const barcodePattern = /^[A-Za-z0-9()]+$/;
    // Optionally allow GS1-128 prefix ]C1
    const cleanedBarcode = barcode.startsWith(']C1') ? barcode.substring(3) : barcode;
    const isBarcodeValid = cleanedBarcode.trim().length >= 8 && barcodePattern.test(cleanedBarcode);
    const isValid = itemNumber.trim() !== '' && barcode.trim() !== '' && uom.trim() !== '' && isBarcodeValid;
    setIsFormValid(isValid);
  }, [itemNumber, barcode, uom]);

  const handleSave = async () => {
    setErrorMessage('');
    if (barcode.trim().length < 8) {
      setErrorMessage(t('barcodeTooShort'));
      return;
    }
    if (!uom) {
      setErrorMessage(t('pleaseSelectUom'));
      return;
    }
    const request: AddOrUpdateBarcodeMappingRequest = {
      ItemNumber: itemNumber,
      Barcode: barcode,
      Uom: uom,
    };
    if (mapping && mapping.Id) {
      request.Id = mapping.Id;
    }
    setIsLoading(true);
    try {
      await onSave(request);
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'message' in error
          ? (error as Error).message
          : t('unknownError');
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScanDetected = (code: string) => {
    setBarcode(code);
    setIsScannerOpen(false);
  };

  const handleScanError = (err: Error) => {
    setErrorMessage(err.message);
    setIsScannerOpen(false);
  };

  return (
    <>
      <Dialog open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth>
        <DialogTitle sx={{ m: 4, p: 4 }}>
          {mapping ? t('editMapping') : t('addMapping')}
          <IconButton onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {helperMessage && (
            <Typography variant='textMD'
              sx={{ my: 4, mx: 6 }}>{helperMessage}</Typography>
          )}
          <Stack spacing={3}
            sx={{ alignItems: 'center' }}>
            {mapping ? (
              <TextField
                label={t('itemNumber')}
                value={`${itemNumber} - ${description}`}
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  '@media (max-width: 600px)': { width: '90%' },
                }}
                disabled
              />
            ) : (
              <Autocomplete
                freeSolo
                value={selectedItem}
                options={itemOptions}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.itemNumber} - ${opt.description}`}
                onInputChange={(_, val) => setSearchTerm(val)}
                onChange={(_, val) => typeof val !== 'string' && setSelectedItem(val)}
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  '@media (max-width: 600px)': { width: '90%' },
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params}
                    label={t('itemNumberOrDescription')}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: true,
                    }}
                  />
                )}
              />
            )}

            <FormControl 
              sx={{
                width: '100%',
                maxWidth: '300px',
                '@media (max-width: 600px)': { width: '90%' },
              }}
              size="small">
              <InputLabel>{t('uom')}</InputLabel>
              <Select value={uom}
                label={t('uom')}
                onChange={(e) => setUom(e.target.value)}
                disabled={!itemNumber}>
                {uomOptions.map((opt) => <MenuItem key={opt.code}
                  value={opt.code}>{opt.code}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField
              label={t('barcode')}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              sx={{
                width: '100%',
                maxWidth: '300px',
                '@media (max-width: 600px)': { width: '90%' },
              }}
              error={
                barcode.length > 0 && (
                  !/^[A-Za-z0-9()]+$/.test(barcode.startsWith(']C1') ? barcode.substring(3) : barcode) ||
                  (barcode.startsWith(']C1') ? barcode.substring(3).length : barcode.length) < 8
                )
              }
              helperText={
                barcode.length === 0
                  ? ''
                  : !/^[A-Za-z0-9()]+$/.test(barcode.startsWith(']C1') ? barcode.substring(3) : barcode)
                    ? t('barcodeAlphanumericOrGS1')
                    : (barcode.startsWith(']C1') ? barcode.substring(3).length : barcode.length) < 8
                      ? t('barcodeMinimumLength', { min: 8 })
                      : t('barcodeGS1Hint')
              }
            />

            {allowScanning && <Typography>{t('or')}</Typography>}

            {allowScanning && (
              <Button
                startIcon={<Camera01 />}
                onClick={() => setIsScannerOpen(true)}
                variant="primary"
                size="lg"
              >
                {t('scanBarcode')}
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ mt: 4, mb: 4, mr: 4 }}>
          <Button onClick={onClose}
            variant="secondary"
            size="lg"
            sx={{ padding: '6px 16px' }}
            disabled={isLoading}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSave}
            variant="primary"
            size="lg"
            sx={{ padding: '6px 16px' }}
            disabled={isLoading || !isFormValid}
            startIcon={isLoading ? <CircularProgress size={15} />: undefined}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
      <MessagesSnackbar
        open={snackbarOpen}
        onClose={()=>setSnackbarOpen(false)}
        severity="error" 
        message={errorMessage}
        duration={3000}
      />
      <BarcodeScannerModal
        isScannerOpen={isScannerOpen}
        closeModal={() => setIsScannerOpen(false)}
        onDetected={handleScanDetected}
        onError={handleScanError}
      />
    </>
  );
};

export default BarcodeMappingAddEditModal;
